import React from 'react';
import cn from '@/utils/cn';
import ThemeSelector from './ThemeSelector';
import { ContentIcon, DevIcon, SupportIcon, TCILogo } from './icons';
import type { NavAccess, NavAccessFlags } from './shared/navSections';
import { useSPAUser } from './shared/SPAUser';
import SPAAccountMenu from './shared/SPAAccountMenu';

export interface SysadminWelcomeProps {
  onNavigate: (path: string) => void;
  signOutPath: string;
  userName: string;
}

interface WelcomeCard {
  access?: NavAccess;
  description: string;
  href?: string;
  icon: React.ReactNode;
  label: string;
  routerPath?: string;
  title: string;
}

const CARDS: WelcomeCard[] = [
  {
    access: 'content',
    description: 'Programs, lessons, slides, and curriculum.',
    icon: <ContentIcon />,
    label: 'Content',
    routerPath: '/content',
    title: 'Content View',
  },
  {
    access: 'support',
    description: 'Subscribers, users, licenses, and tools.',
    icon: <SupportIcon />,
    label: 'Support',
    routerPath: '/support',
    title: 'Support View',
  },
  {
    description: 'DevDocs, system status, and diagnostics.',
    icon: <DevIcon />,
    label: 'Dev',
    routerPath: '/dev',
    title: 'Dev View',
  },
];

const cardClasses = cn(
  'tw-group tw-block tw-no-underline tw-rounded-lg tw-p-5',
  'tw-bg-spa-bg-secondary tw-border tw-border-solid tw-border-spa-border-secondary',
  'hover:tw-bg-spa-bg-hover hover:tw-border-spa-border-hover',
  'tw-transition-all tw-duration-150',
);

const CardContent = ({ card }: { card: WelcomeCard }) => (
  <>
    <div
      className={cn(
        'tw-w-10 tw-h-10 tw-rounded-lg tw-mb-3',
        'tw-flex tw-items-center tw-justify-center',
        'tw-bg-spa-bg-tertiary tw-text-spa-secondary',
        'group-hover:tw-text-spa-accent-text group-hover:tw-bg-spa-accent-bg-hover',
        'tw-transition-colors tw-duration-150',
      )}
    >
      {card.icon}
    </div>
    <div className="tw-text-sm tw-font-medium tw-text-spa-on-bg tw-mb-1">
      {card.label}
    </div>
    <div className="tw-text-xs tw-text-spa-secondary tw-leading-relaxed">
      {card.description}
    </div>
  </>
);

// AIDEV-NOTE: SPA-specific welcome page. Uses navigate() for Content/Dev (client-side routing),
// <a href> for Support (full page navigation to legacy Rails page).
// There is no top bar anywhere in the SPA, so this rail-less page absorbs its jobs itself:
// the logo becomes the hero mark and theme + account sit bottom-left, launcher-style.
const visibleCards = (flags: NavAccessFlags) => CARDS.filter((card) => {
  if (card.access === 'content') return flags.canManageContent;
  if (card.access === 'support') return flags.canManageSupport;

  return true;
});

const SysadminWelcome = ({ onNavigate, signOutPath, userName }: SysadminWelcomeProps) => {
  const cards = visibleCards(useSPAUser());

  return (
    <div
      className={cn(
        'tw-min-h-screen tw-relative',
        'tw-box-border tw-bg-spa-bg tw-flex tw-flex-col tw-items-center tw-px-6 tw-pt-[15vh]',
      )}
    >
      {/* The rail footer's exact footprint (w-80, p-2, name left / theme right) so these
        controls sit at identical coordinates on every page — just without the rail behind. */}
      <div
        className={cn(
          'tw-absolute tw-bottom-0 tw-left-0 tw-box-border tw-w-80 tw-p-2',
          'tw-flex tw-items-center tw-justify-between tw-gap-1',
          'tw-bg-spa-bg-secondary tw-border-0 tw-border-r tw-border-t tw-border-solid',
          'tw-border-spa-border tw-rounded-tr-lg',
        )}
      >
        <SPAAccountMenu className="tw-flex-1" signOutPath={signOutPath} userName={userName} />
        <ThemeSelector />
      </div>
      <div className="tw-text-center tw-mb-10">
        <div aria-hidden="true" className="tw-mb-6 tw-flex tw-justify-center">
          <TCILogo className="tw-h-7 tw-w-auto tw-fill-spa-logo-fill" />
        </div>
        <h1 className="tw-text-2xl tw-font-semibold tw-text-spa-heading tw-mb-1 tw-mt-0">
          Welcome back, {userName}
        </h1>
        <p className="tw-text-sm tw-text-spa-secondary tw-m-0">
          Select a workspace to continue.
        </p>
      </div>
      <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-max-w-3xl tw-w-full">
        {cards.map(card => (
          card.routerPath ? (
            <button
              key={card.title}
              className={cn(cardClasses, 'tw-text-left tw-cursor-pointer tw-w-full')}
              type="button"
              onClick={() => onNavigate(card.routerPath as string)}
            >
              <CardContent card={card} />
            </button>
          ) : (
            <a
              key={card.title}
              className={cardClasses}
              href={card.href}
            >
              <CardContent card={card} />
            </a>
          )
        ))}
      </div>
    </div>
  );
};

export default SysadminWelcome;
